export const PANEL_CLASSES =
  'bg-white rounded-lg border border-ui-divider-lines overflow-hidden flex flex-col';

export const HEADER_CLASSES =
  'px-3.5 py-2.5 font-bold text-body-sm text-ui-text-primary-text border-b border-ui-divider-lines bg-greyscale-11 flex justify-between items-center';

// No margin of its own: rows centre it beside icons and buttons, and the
// stand-alone Core Fields title adds `block mb-2` itself.
export const SECTION_LABEL =
  'text-body-sm font-bold leading-5 text-ui-text-primary-text';

// Empty-slot text and fill states. The dashed outline itself is the
// .slot-dashed / .slot-dashed-hot background in index.css.
export const DROP_ZONE_ACTIVE = 'bg-teal-t10 text-ui-cta-primary';

export const DROP_ZONE_IDLE = 'text-ui-text-primary-text';

// Empty drop zones while a source leaf is armed for click-mapping — signals "click me".
export const DROP_ZONE_TARGET_HOT =
  'text-ui-cta-primary animate-pulse cursor-pointer';

// ── Per-field transforms ──
// Everything transform-related (wand, pills, transformed preview) uses the CTA green.

// Small wand button inside a mapping chip that opens the transform picker.
export const TRANSFORM_ADD_BTN =
  'text-ui-cta-primary hover:text-green-1 leading-none px-1 py-0.5 rounded shrink-0';
export const TRANSFORM_ADD_BTN_ACTIVE =
  'text-green-1 bg-teal-t10 leading-none px-1 py-0.5 rounded shrink-0';
// Shown (not hidden) when the filter is complex, so the feature stays discoverable —
// paired with a tooltip explaining why it's unavailable.
export const TRANSFORM_ADD_BTN_DISABLED =
  'text-ui-text-disabled cursor-not-allowed leading-none px-1 py-0.5 rounded shrink-0 inline-flex';

// A single applied-transform pill shown beneath a mapping chip
// (icon + monospace summary + subtle ×).
export const TRANSFORM_PILL =
  'inline-flex items-center gap-1.5 font-mono text-body-sm bg-teal-t10 text-green-1 rounded-full px-2.5 py-0.5';

// Transformed value shown after the pills (`= 2024-06-26T…`).
export const TRANSFORM_PREVIEW =
  'text-ui-cta-primary text-body-sm max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap';

// Portaled grouped picker popover.
export const TRANSFORM_MENU =
  'bg-white border border-ui-divider-lines rounded shadow-lg overflow-hidden py-1';
export const TRANSFORM_MENU_SECTION =
  'text-body-sm font-bold text-ui-text-primary-text px-3 pt-2 pb-1';
export const TRANSFORM_MENU_ITEM =
  'w-full flex items-center justify-between gap-3 px-3 py-1.5 text-body-sm text-left text-ui-text-primary-text transition-colors focus:outline-none hover:bg-teal-t10';

// ── Stacked slot layout (label on its own line, slot full width below) ──

// The focusable wrapper of every builder row (core, additional, condition):
// keyboard picks return focus here once the slot is mapped.
export const SLOT_ROW =
  'mb-4 rounded focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-t100';

export const SLOT_LABEL =
  'mb-1.5 flex items-center font-mono text-body-sm font-normal text-ui-text-primary-text';

// Mapped chip: light-gray fill, no border, path + value on the left and the
// controls (× | wand) on the right inside the same box.
export const CHIP =
  'flex min-h-[36px] w-full items-center gap-2 rounded-md bg-teal-t10 px-3 py-1.5 font-mono text-body-sm text-ui-text-primary-text';
export const CHIP_WARNING =
  'flex min-h-[36px] w-full items-center gap-2 rounded-md bg-ui-status-bg-warning-bg px-3 py-1.5 font-mono text-body-sm text-yellow-1';

// Vertical rule between the × and the wand inside a chip or empty slot.
export const CHIP_DIVIDER = 'mx-1 h-5 w-px shrink-0 bg-ui-divider-lines';

// Empty slot: dashed full-width box, placeholder left, wand right.
// The dash pattern itself comes from .slot-dashed / .slot-dashed-hot in
// index.css: CSS border-dashed cannot make dashes shorter, an SVG stroke can.
export const EMPTY_SLOT =
  'flex min-h-[36px] w-full items-center rounded-md transition-all';
// The focus ring is drawn by EMPTY_SLOT's wrapper (see EmptySlot), so the
// target itself only drops the browser outline.
export const EMPTY_SLOT_TEXT =
  'flex-1 px-3 py-1.5 text-left text-body-sm focus:outline-none';

// The Code editor's floor, and the height the panel has always had. It grows
// from here to whatever the filter needs, so a short filter looks the same as
// before and a long one is never scrolled inside its own box.
export const CODE_EDITOR_MIN_HEIGHT = 360;
